// Tab Resumen — KPIs operacionales + tendencia 12 meses + alertas.

import React, { useEffect, useState } from 'react';
import {
    Box, Paper, Typography, Grid, Skeleton, Alert, Button, List, ListItem, ListItemText,
} from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import { useNavigate } from 'react-router-dom';
import { dashEjecResumenJWT } from '../../api/endpoints';

const PRIMARY = '#1769aa';
const BR = 4;
const fmtNum = (n) => Number(n || 0).toLocaleString('es-AR');

const PRESET_LABEL = { mes: 'Mes actual', trimestre: 'Últimos 3 meses', ano: 'Año actual', anoAnt: 'Año anterior', custom: 'Período seleccionado' };

export default function TabResumen({ desde, hasta, preset }) {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancel = false;
        setLoading(true);
        dashEjecResumenJWT(desde, hasta).then(r => { if (!cancel && r?.ok) setData(r.data); }).finally(() => { if (!cancel) setLoading(false); });
        return () => { cancel = true; };
    }, [desde, hasta]);

    if (loading) {
        return (
            <Box>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    {[1, 2, 3, 4, 5, 6].map(i => (
                        <Grid key={i} size={{ xs: 6, sm: 4, md: 2 }}>
                            <Skeleton variant="rounded" height={86} sx={{ borderRadius: `${BR}px` }} />
                        </Grid>
                    ))}
                </Grid>
                <Skeleton variant="rounded" height={260} sx={{ borderRadius: `${BR}px` }} />
            </Box>
        );
    }
    if (!data) return <Alert severity="error">No se pudieron cargar los datos.</Alert>;

    const op = data.operacional || {};
    const al = data.alertas || {};
    const personalJub = al.personalProximoJubilar || [];

    return (
        <Box>
            {/* KPIs */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
                {[
                    { label: 'Judiciales en trámite', value: fmtNum(op.judicialesVivos), color: '#1565c0', go: '/expedientes?tab=0' },
                    { label: 'Administrativos', value: fmtNum(op.administrativos), color: '#e65100', go: '/expedientes?tab=1' },
                    { label: `Iniciados — ${PRESET_LABEL[preset] || 'Período'}`, value: fmtNum(op.iniciadosPeriodo), color: '#2e7d32' },
                    { label: 'Finalizados (total acumulado)', value: fmtNum(op.finalizadosTotal), color: '#7b1fa2' },
                    { label: 'Iniciados en el año', value: fmtNum(op.iniciadosAno), color: '#0277bd' },
                    { label: 'En plan de pago', value: fmtNum(op.enPlanPago), color: '#f57c00' },
                ].map((k, i) => (
                    <Grid key={i} size={{ xs: 6, sm: 4, md: 2 }}>
                        <Paper sx={{ p: 1.5, borderRadius: `${BR}px`, height: '100%', cursor: k.go ? 'pointer' : 'default' }}
                            onClick={() => k.go && navigate(k.go)}>
                            <Typography variant="caption" sx={{ color: '#666', fontSize: '0.72rem', textTransform: 'uppercase' }}>
                                {k.label}
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: k.color, mt: 0.5 }}>{k.value}</Typography>
                        </Paper>
                    </Grid>
                ))}
            </Grid>

            {/* Tendencia 12 meses */}
            <Paper sx={{ p: 2, borderRadius: `${BR}px`, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: PRIMARY }}>
                    Tendencia mensual — {PRESET_LABEL[preset] || 'Período'}
                </Typography>
                {(data.tendencia || []).length > 0 ? (
                    <LineChart
                        height={280}
                        xAxis={[{ scaleType: 'point', data: (data.tendencia || []).map(t => t.mes) }]}
                        series={[
                            { data: (data.tendencia || []).map(t => Number(t.jud_iniciados || 0)), label: 'Judiciales iniciados', color: '#1565c0' },
                            { data: (data.tendencia || []).map(t => Number(t.adm_iniciados || 0)), label: 'Administrativos iniciados', color: '#e65100' },
                        ]}
                        margin={{ top: 20, right: 20, bottom: 30, left: 50 }}
                    />
                ) : <Typography color="text.secondary">Sin datos</Typography>}
            </Paper>

            {/* Alertas */}
            <Grid container spacing={2}>
                {al.documentosMiFirma > 0 && (
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Paper sx={{ p: 2, borderRadius: `${BR}px`, border: '2px solid #c62828', background: '#ffebee' }}>
                            <Typography variant="caption" sx={{ color: '#c62828', textTransform: 'uppercase', fontWeight: 700 }}>
                                ✍️ Documentos pendientes de firma
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 700, color: '#c62828', mt: 0.5 }}>
                                {al.documentosMiFirma}
                            </Typography>
                            <Button size="small" onClick={() => navigate('/panel-firma')} sx={{ mt: 1, color: '#c62828', textTransform: 'none' }}>
                                Ir al Panel de Firma →
                            </Button>
                        </Paper>
                    </Grid>
                )}
                {personalJub.length > 0 && (
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Paper sx={{ p: 2, borderRadius: `${BR}px`, border: '1px solid #ed6c02', background: '#fff3e0' }}>
                            <Typography variant="caption" sx={{ color: '#e65100', textTransform: 'uppercase', fontWeight: 700 }}>
                                ⏰ Personal próximo a jubilar (90 días) — {personalJub.length}
                            </Typography>
                            <List dense sx={{ mt: 0.5 }}>
                                {personalJub.map((p, i) => (
                                    <ListItem key={i} disablePadding sx={{ py: 0.25 }}>
                                        <ListItemText
                                            primary={<Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>{p.nombre}</Typography>}
                                            secondary={
                                                <Typography variant="caption" sx={{ color: '#666', fontSize: '0.72rem' }}>
                                                    {p.Funcion || '—'} · {p.Area || '—'} · cumple en {p.dias_para_jub} día(s)
                                                </Typography>
                                            }
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Paper>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
}
